import React, { useEffect, useState } from 'react';
import moment from 'moment';
import {
  Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Paper, Typography, CircularProgress, IconButton, Collapse, Box, TextField,
  Button, Drawer, FormControl, FormLabel, RadioGroup, FormControlLabel, Radio, Divider,
  Icon,
  Tooltip,
  Switch,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { KeyboardArrowDown, KeyboardArrowUp, MoreVert } from '@mui/icons-material';
import { useFetch } from '../../../hooks/useFetch';
import { ContractStateChip } from './ContractState/ContractStateChip';
import { ContractAdminOptions } from './ContractAdminOptions/ContractAdminOptions';
import { fields, mainFields, extraFields } from './ContractAdminFields/ContractAdminFields';
import { getRowColor } from './ContractState/ContractRowColor';
import { startContractsActive } from '../../../reducers/contracts/contractsActions';
import { ExportData } from '../../ExportData/ExportData';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';

export const ContractsAdminScreen = () => {
  const dispatch = useDispatch();
  const activeContract = useSelector(state => state.contracts.activeContract);
  const { data, loading } = useFetch(`contracts?fields=${fields.join(",")}`);

  const [openRow, setOpenRow] = useState(null);
  const [search, setSearch] = useState('');
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [stateFilter, setStateFilter] = useState(null);
  const [endDateSort, setEndDateSort] = useState(null);
  const [deviceSort, setDeviceSort] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [hideFinishedContracts, setHideFinishedContracts] = useState(true);


  const clearFilters = () => {
    setStateFilter(null);
    setEndDateSort(null);
    setDeviceSort(null);
    setHideFinishedContracts(false);
  };
  const alphabeticComparator = (a, b) => {
    const nameA = a.name?.toLowerCase() || '';
    const nameB = b.name?.toLowerCase() || '';
    return nameA.localeCompare(nameB, 'es');
  }

  const handleActivateContract = (id) => {
    if (activeContract?.id !== id) {
      dispatch(startContractsActive(id));
    }
  };

  if (loading) return <CircularProgress sx={{ mt: 4 }} />;

  let filteredData = [...data];

  if (hideFinishedContracts) {
    filteredData = filteredData.filter(c => c.contract_state !== 1);
  }

  filteredData.sort((a, b) => {
    // 1. Búsqueda: coincidencias primero
    if (search) {
      const aMatch = a.name?.toLowerCase().includes(search.toLowerCase()) ? 0 : 1;
      const bMatch = b.name?.toLowerCase().includes(search.toLowerCase()) ? 0 : 1;
      if (aMatch !== bMatch) return aMatch - bMatch;
    }

    // 2. Filtro de estado activo: el estado seleccionado va primero
    if (stateFilter !== null) {
      const aMatch = a.contract_state?.toString() === stateFilter ? 0 : 1;
      const bMatch = b.contract_state?.toString() === stateFilter ? 0 : 1;
      if (aMatch !== bMatch) return aMatch - bMatch;
    }

    // 3. Siempre: contract_state ascendente
    const stateOrder = { 0: 0, 2: 1, 1: 2 };
    const stateDiff = (stateOrder[a.contract_state] ?? 99) - (stateOrder[b.contract_state] ?? 99);
    if (stateDiff !== 0) return stateDiff;

    // 4. Siempre: alfabético
    return alphabeticComparator(a, b);
  });


  if (endDateSort) {
    const now = moment();
    const futureDate = moment().add(Number(endDateSort), 'months');

    filteredData = filteredData.filter(contract => {
      const endDate = moment(contract.end_date);
      return endDate.isBetween(now, futureDate, undefined, '[]');
    });
  }


  if (deviceSort) {
    filteredData.sort((a, b) => (b[deviceSort] || 0) - (a[deviceSort] || 0));
  }
  const checkIfContractExpired = (contract) => {
    if (!contract.end_date) return false;
    const endDate = moment(contract.end_date);
    /*
    console.log("----------------------------------------------------------------");
    console.log("Nombre del contrato: ", contract.name);
    console.log("fecha de vencimiento: ", endDate);
    console.log("fecha actual: ", moment());
    console.log("¿Contrato vencido?", endDate.isBefore(moment()));
    console.log("----------------------------------------------------------------");
    */

    return endDate.isBefore(moment());
  }
  return (
    <>

      <TableContainer component={Paper} sx={{ mt: 2, maxHeight: 'calc(100vh - 200px)' }}>
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <TextField
            label="Buscar contrato..."
            variant="outlined"
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <Box display="flex" alignItems="center" gap={2}>
            <Button variant="contained" onClick={() => setExportDialogOpen(true)}>
              Exportar
            </Button>

            <Button variant="contained" onClick={() => setFilterDrawerOpen(true)}>
              Filtros
            </Button>
          </Box>
        </Box>

        <Drawer anchor="right" open={filterDrawerOpen} onClose={() => setFilterDrawerOpen(false)}>
          <Box sx={{ width: 400, p: 10 }}>
            <Typography variant="h5" gutterBottom>Filtros</Typography>

            <FormControl component="fieldset" sx={{ mb: 2 }}>
              <FormLabel>Ocultar contratos de baja</FormLabel>
              <FormControlLabel
                control={
                  <Switch
                    checked={hideFinishedContracts}
                    onChange={(e) => setHideFinishedContracts(e.target.checked)}
                  />
                }
                label={hideFinishedContracts ? 'Sí' : 'No'}
              />
            </FormControl>

            <FormControl component="fieldset" sx={{ mb: 2 }}>
              <FormLabel>Estado del contrato</FormLabel>
              <RadioGroup value={stateFilter} onChange={(e) => setStateFilter(e.target.value)}>
                <FormControlLabel value="1" control={<Radio />} label="Baja" />
                <FormControlLabel value="2" control={<Radio />} label="Vencido" />
                <FormControlLabel value="0" control={<Radio />} label="En curso" />
              </RadioGroup>
            </FormControl>

            <FormControl component="fieldset" sx={{ mb: 2 }}>
              <FormLabel>Fecha de vencimiento</FormLabel>
              <RadioGroup value={endDateSort} onChange={(e) => setEndDateSort(e.target.value)}>
                <FormControlLabel value="1" control={<Radio />} label="Próximo mes" />
                <FormControlLabel value="3" control={<Radio />} label="Próximos 3 meses" />
                <FormControlLabel value="6" control={<Radio />} label="Próximos 6 meses" />
              </RadioGroup>
            </FormControl>


            <Divider sx={{ my: 2 }} />
            <FormControl component="fieldset">
              <FormLabel>Ordenar por número de equipos</FormLabel>
              <RadioGroup value={deviceSort} onChange={(e) => setDeviceSort(e.target.value)}>
                <FormControlLabel value="cm_devices" control={<Radio />} label="CM" />
                <FormControlLabel value="nodes_devices" control={<Radio />} label="Nodos" />
                <FormControlLabel value="telemeasures_devices" control={<Radio />} label="Telemedidas" />
              </RadioGroup>
            </FormControl>

            <Button variant="contained" fullWidth sx={{ mt: 3 }} onClick={clearFilters}>
              Limpiar filtros
            </Button>
          </Box>
        </Drawer>

        <Table size={'small'} stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell />
              {Object.keys(mainFields).map(field => (
                <TableCell key={field} sx={{ fontWeight: 'bold' }}>
                  {mainFields[field]}
                </TableCell>
              ))}
              <TableCell />
            </TableRow>
          </TableHead>

          <TableBody>
            {filteredData.map((row, idx) => {
              const isOpen = openRow === idx;
              const rowColor = getRowColor(row.contract_state);
              const isActive = activeContract?.id === row.id;

              return (
                <React.Fragment key={idx}>
                  <TableRow sx={{ backgroundColor: rowColor, borderBottom: '3px solid rgba(0, 0, 0, 0.1)' }}>
                    <TableCell>
                      <IconButton onClick={() => setOpenRow(isOpen ? null : idx)}>
                        {isOpen ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                      </IconButton>
                    </TableCell>
                    {Object.keys(mainFields).map(field => (
                      <TableCell key={field}>
                        {field === "contract_state" ? (
                          <ContractStateChip value={row[field]} />
                        ) : field === "name" ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Button
                              onClick={() => handleActivateContract(row.id)}
                              variant={isActive ? 'contained' : 'outlined'}
                              size="medium"
                              color={isActive ? 'success' : 'primary'}
                              sx={{
                                textTransform: 'none',
                                fontWeight: 'bold',
                                backgroundColor: isActive ? 'primary.main' : 'grey.100',
                                color: isActive ? 'white' : 'text.primary',
                                '&:hover': {
                                  backgroundColor: isActive ? 'primary.dark' : 'grey.200',
                                },
                              }}
                            >
                              {row.name}
                            </Button>
                            {checkIfContractExpired(row) && (
                              <Tooltip title="Fecha de vencimiento superada. Se recomienda revisar la fecha de vencimiento del contrato"  >
                                <WarningRoundedIcon sx={{ color: '#d84f26' }} />
                              </Tooltip>
                            )}

                          </Box>
                        ) : (
                          row[field] ?? '-'
                        )}
                      </TableCell>
                    ))}
                    <TableCell>
                      {isActive && (
                        <>
                          <IconButton onClick={(e) => setMenuAnchor(e.currentTarget)}>
                            <MoreVert />
                          </IconButton>
                          <ContractAdminOptions anchorEl={menuAnchor} onClose={() => setMenuAnchor(null)} />
                        </>
                      )}
                    </TableCell>
                  </TableRow>

                  <TableRow>
                    <TableCell colSpan={Object.keys(mainFields).length + 2} sx={{ p: 0 }}>
                      <Collapse in={isOpen} timeout="auto" unmountOnExit>
                        <Box sx={{ margin: 2, backgroundColor: rowColor }}>
                          <Table size="small">
                            <TableBody>
                              {Object.keys(extraFields).map(field => (
                                <TableRow key={field}>
                                  <TableCell sx={{ fontWeight: 'bold', width: 200 }}>
                                    {extraFields[field]}
                                  </TableCell>
                                  <TableCell>{row[field] ?? '-'}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <ExportData
        open={exportDialogOpen}
        onClose={() => setExportDialogOpen(false)}
        fields={{ ...mainFields, ...extraFields }}
        contracts={filteredData}
      />
    </>

  );
};
